import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { CampoSenha } from "@/components/shared/campo-senha";
import { useAuth } from "@/lib/auth-context";

const MINIMO = 8;

/**
 * Trocar a própria palavra-passe (Sprint V, 11/10 — compliance).
 *
 * É a senha do PRÓPRIO, pela sessão ativa (`updatePassword` → GoTrue) — nada a
 * ver com a RPC `admin_redefinir_senha`, que é para um admin redefinir a de
 * outros. Por isso qualquer utilizador aprovado o pode fazer.
 *
 * Sem `<form onSubmit>`: é a convenção do projeto (o Enter em comboboxes
 * submeteria por acidente), e aqui o botão de ação é quem grava.
 */
export function DialogoMudarSenha({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
}) {
  const { updatePassword } = useAuth();
  const [nova, setNova] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [aGravar, setAGravar] = useState(false);

  const curta = nova.length < MINIMO;
  const diferentes = confirmar.length > 0 && nova !== confirmar;
  const invalido = curta || nova !== confirmar;

  function limpar() {
    setNova("");
    setConfirmar("");
    setAGravar(false);
  }

  async function aoGravar() {
    if (invalido) return;
    setAGravar(true);
    const { error } = await updatePassword(nova);
    if (error) {
      setAGravar(false);
      toast.error(error.message ?? "Não foi possível mudar a palavra-passe.");
      return;
    }
    toast.success("Palavra-passe alterada.");
    limpar();
    onOpenChange(false);
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(aberto) => {
        if (!aberto) limpar();
        onOpenChange(aberto);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mudar a minha palavra-passe</AlertDialogTitle>
          <AlertDialogDescription>
            Define uma nova palavra-passe (mínimo {MINIMO} caracteres). Passa a
            entrar com ela de imediato.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="nova-senha">Nova palavra-passe</Label>
            <CampoSenha
              id="nova-senha"
              value={nova}
              onChange={(e) => setNova(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              autoComplete="new-password"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="confirmar-senha">Confirmar</Label>
            <CampoSenha
              id="confirmar-senha"
              value={confirmar}
              onChange={(e) => setConfirmar(e.target.value)}
              placeholder="Repetir a nova palavra-passe"
              autoComplete="new-password"
            />
            {diferentes && (
              <p className="text-destructive text-xs">
                As palavras-passe não coincidem.
              </p>
            )}
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            disabled={invalido || aGravar}
            onClick={(e) => {
              e.preventDefault();
              void aoGravar();
            }}
          >
            Mudar senha
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
